import { isApple, restoreApple } from '../applePurchases';
import useReducedMotion from '../hooks/useReducedMotion';
import DeleteAccountDialog from './DeleteAccountDialog';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { COLOR_OPTIONS, FREE_COLOR_KEYS, ANIMAL_OPTIONS, SUPPORT_EMAIL } from '../constants';
import CrisisResources from './CrisisResources';
import PrivacyPolicy from './PrivacyPolicy';
import About from './About';
import CogIcon from './CogIcon';

const labelCls = 'text-[0.625rem] font-bold text-white/50 uppercase mb-2 block tracking-widest';

// The "random" option's tile flips through the animal emojis so it visibly means
// "a random mix", instead of a single static icon.
const RANDOM_CYCLE_EMOJIS = Object.entries(ANIMAL_OPTIONS)
  .filter(([key]) => key !== 'random')
  .map(([, data]) => data.emoji);

const CyclingAnimalTile = () => {
  const reducedMotion = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reducedMotion) return undefined;
    const id = setInterval(
      () => setI((n) => (n + 1) % RANDOM_CYCLE_EMOJIS.length),
      450
    );
    return () => clearInterval(id);
  }, [reducedMotion]);
  return <span aria-hidden="true">{reducedMotion ? '🔀' : RANDOM_CYCLE_EMOJIS[i]}</span>;
};

const TIMER_PRESETS = [0, 1, 2, 5, 10, 20]; // minutes; 0 = off

const SettingsPanel = ({ settings, updateSettings, onSetTimer, onOpenChange, onOpenStats, openSignal = 0, closeSignal = 0, kidsDemo = false, demoHighlight = false, onDemoControlsDone, onSignOut, onDeleteAccount, onManageSubscription, isPro = false, hasBillingAccount = false, onProRequired }) => {
  const reducedMotion = useReducedMotion();
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState('');
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'crisis' | 'privacy' | null
  const kidsRowRef = useRef(null);
  const scrollRef = useRef(null); // the scrolling body of the panel

  // Tell the app when the panel opens/closes so it can freeze the orbs.
  useEffect(() => { if (onOpenChange) onOpenChange(open); }, [open, onOpenChange]);

  // Let the app open/close the panel programmatically (swipe-up gesture).
  useEffect(() => { if (openSignal > 0) setOpen(true); }, [openSignal]);
  useEffect(() => { if (closeSignal > 0) setOpen(false); }, [closeSignal]);

  // --- Interactive Kids-Mode demo -----------------------------------------
  // The cog pulses to invite a tap; once the user opens the panel we scroll to
  // the Kids switch and pulse it until they flip it on. Turning it on selects
  // the rotating "random" animals, then the panel closes and the demo moves on.
  // Entitlement is part of the rendered state, not only the click handler. A
  // stale saved `kidsMode: true` must never reveal paid content after access
  // ends or while entitlement is still loading. The one exception is the
  // onboarding demo (`kidsDemo`): it lets a free user preview the real switch,
  // so kidsOn also lights up there even without Pro.
  const kidsOn = (isPro || kidsDemo) && !!settings.kidsMode;
  const colourEntries = Object.entries(COLOR_OPTIONS).filter(
    ([name]) => isPro || FREE_COLOR_KEYS.includes(name)
  );
  const cogPulse = kidsDemo && !open && !kidsOn;      // invite the user to open Settings
  const kidsPulse = kidsDemo && open && !kidsOn;      // invite them to flip Kids Mode on

  // When the panel opens during the kids demo, glide the Kids switch into view.
  // We animate scrollTop ourselves (over ~1.8s, eased) rather than using the
  // browser's smooth scrollIntoView — in the iOS WebView that jumps almost
  // instantly, too fast for the user to register the controls it passes.
  useEffect(() => {
    if (!(kidsDemo && open)) return;
    let raf = 0;
    const t = setTimeout(() => {
      const c = scrollRef.current;
      const row = kidsRowRef.current;
      if (!c || !row) return;
      const cRect = c.getBoundingClientRect();
      const rRect = row.getBoundingClientRect();
      const target = c.scrollTop + (rRect.top - cRect.top) - (c.clientHeight - rRect.height) / 2;
      if (reducedMotion) { c.scrollTop = target; return; }
      const startTop = c.scrollTop;
      const dist = target - startTop;
      if (Math.abs(dist) < 2) return;
      const duration = 1800;
      const start = performance.now();
      const ease = (p) => 0.5 - Math.cos(Math.PI * p) / 2; // easeInOutSine
      const stepFn = (now) => {
        const p = Math.min(1, (now - start) / duration);
        c.scrollTop = startTop + dist * ease(p);
        if (p < 1) raf = requestAnimationFrame(stepFn);
      };
      raf = requestAnimationFrame(stepFn);
    }, 350);
    return () => { clearTimeout(t); if (raf) cancelAnimationFrame(raf); };
  }, [kidsDemo, open, reducedMotion]);

  // Once they switch Kids Mode on, linger so the flip is visible, then close the
  // panel and hand back to the app (which gives them time to tap the animals).
  useEffect(() => {
    if (!(kidsDemo && open && kidsOn)) return;
    const id = setTimeout(() => {
      setOpen(false);
    }, 700);
    return () => clearTimeout(id);
  }, [kidsDemo, open, kidsOn]);

  // --- First-launch demo: flash the four core controls and finish once the
  // user has touched all of them, or after 5s, whichever comes first. ---
  const [demoTouched, setDemoTouched] = useState({});
  const demoDoneRef = useRef(false);
  const finishDemoControls = useCallback(() => {
    if (demoDoneRef.current) return;
    demoDoneRef.current = true;
    setOpen(false);
    if (onDemoControlsDone) onDemoControlsDone();
  }, [onDemoControlsDone]);
  // In the demo, each touched control stops flashing; once the user has changed
  // ALL FOUR the panel closes itself and the demo moves on. No time limit.
  const markDemoTouched = (key) => {
    if (!demoHighlight) return;
    setDemoTouched((t) => (t[key] ? t : { ...t, [key]: true }));
  };
  useEffect(() => {
    if (!(demoHighlight && open)) return;
    demoDoneRef.current = false;
    setDemoTouched({});
  }, [demoHighlight, open]);
  useEffect(() => {
    if (demoHighlight && open && ['color', 'size', 'spacing', 'speed'].every((k) => demoTouched[k])) {
      // Linger a moment after the last change so they can see what they set
      // before the panel closes.
      const id = setTimeout(finishDemoControls, 1800);
      return () => clearTimeout(id);
    }
  }, [demoTouched, demoHighlight, open, finishDemoControls]);
  // A control is "highlighted" by the demo until the user touches it.
  const isFlashing = (key) => demoHighlight && open && !demoTouched[key];
  const flashCls = (key) => labelCls + (isFlashing(key) ? ' demo-flash' : '');
  const rowCls = (key) => 'mb-4' + (isFlashing(key) ? ' demo-row' : '');

  // Closing early (tap outside, or the ✕) must still count as "done" if a
  // demo-completion condition is already met - otherwise it silently
  // discards progress (demoTouched, the pending kids-mode advance) and the
  // user has to redo it when they reopen the panel, since the demo never
  // actually moved on.
  const closeSettings = () => {
    if (demoHighlight && ['color', 'size', 'spacing', 'speed'].every((k) => demoTouched[k])) {
      finishDemoControls();
    } else if (kidsDemo && kidsOn) {
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  const handleDelete = () => setModal('delete');

  const handleManageSubscription = async () => {
    try {
      await onManageSubscription?.();
    } catch (error) {
      if (error?.code === 'functions/failed-precondition') {
        alert('No billing account was found. Please try again after your checkout has completed.');
      } else {
        alert('Could not open your subscription settings. Please try again.');
      }
    }
  };

  return (
    <div
      className="absolute z-controls flex flex-col items-end gap-2 pointer-events-none"
      style={{
        // Bound the column by real top+bottom insets (not vh, which is unreliable
        // in the iOS WebView on rotation) so the panel below can size to the
        // actual available height and scroll internally — works in landscape.
        top: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        right: 'calc(env(safe-area-inset-right, 0px) + 1rem)',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Settings"
        className={`shrink-0 pointer-events-auto w-11 h-11 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-xl border shadow-2xl active:scale-95 transition-transform ${cogPulse ? 'demo-cog border-white/40' : 'border-white/15'}`}
      >
        <CogIcon className="w-6 h-6" />
      </button>

      {/* Tap anywhere outside the panel to close it. */}
      {open && (
        <div className="fixed inset-0 pointer-events-auto" onClick={closeSettings} aria-hidden />
      )}

      {open && (
        <div className="relative bg-black/40 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl max-w-[17.5rem] min-h-0 overflow-hidden pointer-events-auto flex flex-col">
          {/* Scrolling lives on this inner box, which has NO backdrop-filter:
              backdrop-filter + overflow-scroll on the same element breaks touch
              scrolling in the iOS WebView. */}
          <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto p-4"
            style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
          >
          <div className="flex items-center justify-between mb-3">
            <span className="text-white font-bold text-sm tracking-wide">Settings</span>
            <button
              onClick={closeSettings}
              aria-label="Close settings"
              className="cross-pulse w-11 h-11 -mr-1 -mt-1 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-xl leading-none transition-colors"
            >
              ✕
            </button>
          </div>

          {kidsOn ? (
            <div className="mb-4">
              <label className={labelCls}>Animal</label>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(ANIMAL_OPTIONS).map(([key, data]) => {
                  const isRandom = key === 'random';
                  const selected = settings.selectedAnimal === key;
                  return (
                  <button
                    key={key}
                    onClick={() => updateSettings({ selectedAnimal: key })}
                    className={`min-h-11 min-w-11 text-xl p-1 rounded-lg transition-all ${
                      selected
                        ? 'bg-white/20 scale-110'
                        : isRandom
                          ? 'opacity-100 ring-2 ring-purple-400/80 bg-purple-500/15 shadow-[0_0_12px_rgba(168,85,247,0.55)]'
                          : 'opacity-40 hover:opacity-100'
                    }`}
                    aria-pressed={selected}
                    aria-label={isRandom ? 'Random animals' : key}
                    title={isRandom ? 'Random: a different animal each time' : key}
                  >
                    {isRandom ? <CyclingAnimalTile /> : data.emoji}
                  </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={rowCls('color')}>
              <label className={flashCls('color')}>Orb Colour</label>
              <div className="grid grid-cols-4 gap-2">
                {colourEntries.map(([name, data]) => (
                  <button
                    key={name}
                    onClick={() => { updateSettings({ orbColor: name }); markDemoTouched('color'); }}
                    className="flex h-11 w-11 items-center justify-center rounded-full"
                    aria-pressed={settings.orbColor === name}
                    aria-label={name === 'multicolor' ? 'Multicolour' : name}
                    title={name === 'multicolor' ? 'Multicolour: orbs change colour each tap' : name}
                  >
                    <span aria-hidden="true" className={`h-6 w-6 rounded-full border-2 ${settings.orbColor === name ? 'border-white' : 'border-transparent'}`} style={{ background: data.gradient }} />
                  </button>
                ))}
                {!isPro && (
                  <button
                    type="button"
                    // During the demo's colour/size/spacing/speed step, this is
                    // just a visual "there's more, and it's locked" cue - tapping
                    // it shouldn't interrupt the tour with the full trial pitch.
                    onClick={() => { if (!demoHighlight) onProRequired?.('colours'); }}
                    aria-label="More orb colours, TapTap Pro, locked. Open Pro details."
                    aria-haspopup={demoHighlight ? undefined : 'dialog'}
                    className="flex h-11 min-w-11 items-center justify-center rounded-full border border-violet-300/20 bg-violet-500/10 px-2 text-[0.5rem] font-bold uppercase tracking-wider text-violet-200 transition-colors hover:bg-violet-500/20"
                    title="More colours with TapTap Pro"
                  >
                    + Pro
                  </button>
                )}
              </div>
            </div>
          )}

          <div className={rowCls('size')}>
            <label htmlFor="setting-size" className={flashCls('size')}>Size</label>
            <input
              id="setting-size" type="range" min="1" max="5" step="0.5"
              value={settings.orbSize ?? 3}
              onChange={(e) => { updateSettings({ orbSize: Number(e.target.value) }); markDemoTouched('size'); }}
              className="min-h-11 w-full accent-blue-500"
            />
          </div>

          <div className={rowCls('spacing')}>
            <label htmlFor="setting-spacing" className={flashCls('spacing')}>Spacing</label>
            <input
              id="setting-spacing" type="range" min="0" max="100" step="5"
              value={settings.orbSpacing ?? 40}
              onChange={(e) => { updateSettings({ orbSpacing: Number(e.target.value) }); markDemoTouched('spacing'); }}
              className="min-h-11 w-full accent-blue-500"
            />
          </div>

          <div className={rowCls('speed')}>
            <label htmlFor="setting-speed" className={flashCls('speed')}>Speed: {(settings.reappearSpeed ?? 0.5)}s</label>
            <input
              id="setting-speed" type="range" min="0.1" max="3" step="0.1"
              value={settings.reappearSpeed ?? 0.5}
              onChange={(e) => { updateSettings({ reappearSpeed: Number(e.target.value) }); markDemoTouched('speed'); }}
              className="min-h-11 w-full accent-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className={labelCls}>Timer</label>
            <div className="grid grid-cols-3 gap-2">
              {TIMER_PRESETS.map((m) => (
                <button
                  key={m}
                  onClick={() => {
                    updateSettings({ sessionMinutes: m });
                    if (onSetTimer) onSetTimer(m);
                    if (m) setOpen(false); // close so they can start tapping
                  }}
                  aria-pressed={(settings.sessionMinutes ?? 0) === m}
                  className={`min-h-11 py-1.5 rounded-lg text-[0.6875rem] font-bold uppercase tracking-widest transition-colors ${(settings.sessionMinutes ?? 0) === m ? 'bg-gradient-to-r from-blue-500/40 to-purple-500/40 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
                >
                  {m === 0 ? 'Off' : `${m}m`}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <label className={labelCls + ' mb-0'}>Sound</label>
            <button
              role="switch" aria-label="Sound" aria-checked={settings.audioEnabled ?? true}
              onClick={() => updateSettings({ audioEnabled: !(settings.audioEnabled ?? true) })}
              className={`min-h-11 min-w-11 px-3 py-1 rounded-full text-[0.625rem] font-bold uppercase tracking-widest transition-colors ${(settings.audioEnabled ?? true) ? 'bg-gradient-to-r from-blue-500/40 to-purple-500/40 text-white' : 'bg-white/10 text-white/65'}`}
            >
              {(settings.audioEnabled ?? true) ? 'On' : 'Off'}
            </button>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <span className={labelCls + ' mb-0'}>Haptics</span>
            <button role="switch" aria-label="Haptics" aria-checked={settings.hapticsEnabled !== false}
              onClick={() => updateSettings({ hapticsEnabled: settings.hapticsEnabled === false })}
              className="min-h-11 min-w-11 rounded-full bg-white/10 px-3 text-xs font-bold text-white">
              {settings.hapticsEnabled !== false ? 'On' : 'Off'}
            </button>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <label className={labelCls + ' mb-0'}>Show Progress</label>
            <button
              role="switch" aria-label="Show Progress" aria-checked={!(settings.hideProgress ?? false)}
              onClick={() => updateSettings({ hideProgress: !(settings.hideProgress ?? false) })}
              className={`min-h-11 min-w-11 px-3 py-1 rounded-full text-[0.625rem] font-bold uppercase tracking-widest transition-colors ${!(settings.hideProgress ?? false) ? 'bg-gradient-to-r from-blue-500/40 to-purple-500/40 text-white' : 'bg-white/10 text-white/65'}`}
            >
              {!(settings.hideProgress ?? false) ? 'On' : 'Off'}
            </button>
          </div>

          {(settings.audioEnabled ?? true) && (
            <div className="mb-4">
              <label htmlFor="setting-volume" className={labelCls}>Volume</label>
              <input
                id="setting-volume" type="range" min="0" max="100" step="5"
                value={settings.audioVolume ?? 50}
                onChange={(e) => updateSettings({ audioVolume: Number(e.target.value) })}
                className="min-h-11 w-full accent-blue-500"
              />
            </div>
          )}

          <div ref={kidsRowRef} className={'mb-4 flex items-center justify-between' + (kidsPulse ? ' demo-row' : '')}>
            <label className={labelCls + ' mb-0'}>Kids Mode{!isPro ? ' · Pro' : ''}</label>
            <button
              aria-label={(isPro || kidsDemo) ? 'Toggle kids mode' : 'Kids Mode, TapTap Pro, locked. Open Pro details.'}
              aria-haspopup={(isPro || kidsDemo) ? undefined : 'dialog'}
              aria-pressed={(isPro || kidsDemo) ? kidsOn : undefined}
              onClick={() => {
                if (!isPro && !kidsDemo) { onProRequired?.('kids'); return; }
                updateSettings(kidsOn
                  ? { kidsMode: false }
                  // Turning it on during the demo starts on the rotating animals
                  // so the playful, ever-changing sounds show off Kids Mode.
                  : { kidsMode: true, ...(kidsDemo ? { selectedAnimal: 'random' } : {}) });
              }}
              className={`min-h-11 min-w-16 px-3 py-1 rounded-full text-[0.625rem] font-bold uppercase tracking-widest transition-colors ${kidsOn ? 'bg-gradient-to-r from-blue-500/40 to-purple-500/40 text-white' : !isPro ? 'border border-violet-300/20 bg-violet-500/10 text-violet-200' : 'bg-white/10 text-white/65'}${kidsPulse ? ' demo-switch' : ''}`}
            >
              {kidsOn ? 'On' : (isPro || kidsDemo) ? 'Off' : 'View Pro'}
            </button>
          </div>

          <div className="mb-4 flex items-center justify-between">
            <label className={labelCls + ' mb-0'}>Swipe ↘ for Stats</label>
            <button
              role="switch" aria-label="Swipe gestures" aria-checked={settings.swipeGestures ?? true}
              onClick={() => updateSettings({ swipeGestures: !(settings.swipeGestures ?? true) })}
              className={`min-h-11 min-w-11 px-3 py-1 rounded-full text-[0.625rem] font-bold uppercase tracking-widest transition-colors ${(settings.swipeGestures ?? true) ? 'bg-gradient-to-r from-blue-500/40 to-purple-500/40 text-white' : 'bg-white/10 text-white/65'}`}
            >
              {(settings.swipeGestures ?? true) ? 'On' : 'Off'}
            </button>
          </div>

          <div className="border-t border-white/10 my-3" />

          <button
            onClick={() => { if (onOpenStats) onOpenStats(); setOpen(false); }}
            className="w-full py-2 mb-2 text-white text-[0.625rem] font-bold rounded-xl uppercase tracking-widest bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 transition-all active:scale-95"
          >
            Your Stats
          </button>

          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('TapTap feedback')}&body=${encodeURIComponent('\n\n— Tell us what you think, what you’d like changed, or report a problem.')}`}
            className="block w-full py-2 mb-2 bg-white/5 hover:bg-white/10 text-white/80 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors text-center"
          >
            Send Feedback
          </a>

          <button
            onClick={() => setModal('about')}
            className="w-full py-2 mb-2 bg-white/5 hover:bg-white/10 text-white/80 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors"
          >
            About TapTap
          </button>

          <button
            onClick={() => setModal('crisis')}
            className="w-full py-2 mb-2 bg-white/5 hover:bg-white/10 text-white/80 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors"
          >
            Help &amp; Support
          </button>

          <button
            onClick={() => setModal('privacy')}
            className="w-full py-2 mb-2 bg-white/5 hover:bg-white/10 text-white/80 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors"
          >
            Privacy Policy
          </button>

          {isApple && <>
            <button type="button" disabled={restoreBusy} onClick={async () => {
              setRestoreBusy(true); setRestoreMessage('');
              try { const result = await restoreApple(); setRestoreMessage(result.active ? 'Your Pro subscription has been restored.' : 'No active Apple subscription was found.'); }
              catch (error) { setRestoreMessage(error.message || 'Could not restore purchases.'); }
              finally { setRestoreBusy(false); }
            }} className="w-full min-h-11 py-2 mb-2 bg-white/5 hover:bg-white/10 text-white/80 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors disabled:opacity-60">{restoreBusy ? 'Restoring…' : 'Restore Purchases'}</button>
            {restoreMessage && <p role="status" className="mb-2 text-xs text-white/80">{restoreMessage}</p>}
          </>}
          {hasBillingAccount && (
            <button
              onClick={handleManageSubscription}
              className="w-full py-2 mb-2 bg-white/5 hover:bg-white/10 text-white/80 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors"
            >
              Manage Subscription
            </button>
          )}

          <button
            onClick={onSignOut}
            className="w-full py-2 mb-2 bg-white/5 hover:bg-white/10 text-white/80 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors"
          >
            Sign Out
          </button>

          <button
            onClick={handleDelete}
            className="w-full py-2 bg-red-500/20 hover:bg-red-500/40 text-red-200 text-[0.625rem] font-bold rounded-xl uppercase tracking-widest transition-colors"
          >
            Delete Account
          </button>
          </div>
        </div>
      )}

      {modal === 'delete' && <DeleteAccountDialog onDelete={onDeleteAccount} onClose={() => setModal(null)} />}
      {modal === 'about' && <About onClose={() => setModal(null)} />}
      {modal === 'crisis' && <CrisisResources onClose={() => setModal(null)} />}
      {modal === 'privacy' && <PrivacyPolicy onClose={() => setModal(null)} />}
    </div>
  );
};

export default SettingsPanel;
